/**
 * Google Analytics 4 Provider
 *
 * Implementation of AnalyticsProvider for Google Analytics 4 (GA4).
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4
 */

import type { AnalyticsProvider, GoogleAnalyticsConfig, EventProperties, PageProperties, UserTraits } from '../types'

// GA4 gtag function type
interface Gtag {
  (command: 'js', date: Date): void
  (command: 'config', targetId: string, config?: Record<string, unknown>): void
  (command: 'event', eventName: string, eventParams?: Record<string, unknown>): void
  (command: 'set', config: Record<string, unknown>): void
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
  }
}

/**
 * Create a Google Analytics 4 provider
 *
 * @param config - GA4 configuration
 * @returns AnalyticsProvider instance
 *
 * @example
 * ```tsx
 * import { createGoogleAnalyticsProvider } from '@gjl/analytics/google-analytics';
 *
 * const analytics = createGoogleAnalyticsProvider({
 *   measurementId: 'G-XXXXXXXXXX',
 * });
 *
 * // Use with context provider
 * <AnalyticsProvider provider={analytics}>
 *   <App />
 * </AnalyticsProvider>
 * ```
 */
export function createGoogleAnalyticsProvider(config: GoogleAnalyticsConfig): AnalyticsProvider {
  const { measurementId, debug, disabled, debugMode } = config

  let initialized = false

  // Initialize GA4
  const initialize = () => {
    if (initialized || disabled || typeof window === 'undefined') {
      return
    }

    try {
      // Initialize dataLayer
      window.dataLayer = window.dataLayer || []

      // Define gtag function
      window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer?.push(args)
      }

      // Record start time
      window.gtag('js', new Date())

      // Configure GA4
      window.gtag('config', measurementId, {
        debug_mode: debugMode,
        send_page_view: false, // We handle page views manually
      })

      // Load GA4 script
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
      document.head.appendChild(script)

      script.onload = () => {
        initialized = true
        if (debug) {
          console.log('[Analytics/GA4] Initialized')
        }
      }

      script.onerror = () => {
        if (debug) {
          console.warn('[Analytics/GA4] Failed to load script')
        }
      }
    } catch (error) {
      if (debug) {
        console.warn('[Analytics/GA4] Failed to initialize:', error)
      }
    }
  }

  // Initialize on creation
  if (typeof window !== 'undefined') {
    initialize()
  }

  const track = (event: string, properties?: EventProperties) => {
    if (disabled) return

    if (debug) {
      console.log('[Analytics/GA4] track:', event, properties)
    }

    if (window.gtag) {
      // Convert event name to GA4 convention (snake_case, no hyphens)
      const eventName = event.toLowerCase().replace(/-/g, '_')
      window.gtag('event', eventName, properties)
    }
  }

  const page = (name: string, properties?: PageProperties) => {
    if (disabled) return

    if (debug) {
      console.log('[Analytics/GA4] page:', name, properties)
    }

    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: properties?.title || name,
        page_location: properties?.url || window.location.href,
        page_path: properties?.path || window.location.pathname,
        page_name: name,
        ...properties,
      })
    }
  }

  const identify = (userId: string, traits?: UserTraits) => {
    if (disabled) return

    if (debug) {
      console.log('[Analytics/GA4] identify:', userId, traits)
    }

    if (window.gtag) {
      // Set user ID
      window.gtag('config', measurementId, {
        user_id: userId,
      })

      // Set user properties
      if (traits) {
        window.gtag('set', {
          user_properties: traits,
        })
      }
    }
  }

  const reset = () => {
    if (disabled) return

    if (debug) {
      console.log('[Analytics/GA4] reset')
    }

    if (window.gtag) {
      // Clear user ID
      window.gtag('config', measurementId, {
        user_id: null,
      })

      // Clear user properties
      window.gtag('set', {
        user_properties: null,
      })
    }
  }

  const isReady = () => initialized && window.gtag !== undefined

  return {
    track,
    page,
    identify,
    reset,
    isReady,
  }
}
